import { Button } from "@chakra-ui/react"
import Tooltip from "@pathwright/ui/src/components/tooltip/Tooltip"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { useMemo } from "react"
import {
  useCreateDiscussionLikeMutation,
  useDeleteDiscussionLikeMutation
} from "@pathwright/graphql-client/src/generated"
import { useDiscussionPerms } from "../../permissions"
import { getDiscussionContext } from "./utils"

const DiscussionVote = ({ discussion }) => {
  const { t } = useTranslate()
  const discussionContext = getDiscussionContext(discussion)
  const discussionPerms = useDiscussionPerms(discussion)

  const [createDiscussionLike, createDiscussionLikeState] =
    useCreateDiscussionLikeMutation({
      variables: {
        context: discussionContext,
        discussion_id: discussion.id
      },
      optimisticResponse: {
        __typename: "Mutations",
        createDiscussionLike: {
          __typename: "Discussion",
          ...discussion,
          likes: discussion.likes + 1,
          user_liked: true
        }
      }
    })

  const [deleteDiscussionLike, deleteDiscussionLikeState] =
    useDeleteDiscussionLikeMutation({
      variables: {
        context: discussionContext,
        discussion_id: discussion.id
      },
      optimisticResponse: {
        __typename: "Mutations",
        deleteDiscussionLike: {
          __typename: "Discussion",
          ...discussion,
          likes: discussion.likes - 1,
          user_liked: false
        }
      }
    })

  const isExecutingMutation =
    createDiscussionLikeState.loading || deleteDiscussionLikeState.loading

  const handleToggleVote = () => {
    // Prevent triggering subsequent mutation while awaiting result of executed mutation.
    if (!isExecutingMutation) {
      if (discussion.user_liked) {
        deleteDiscussionLike()
      } else if (discussionPerms.canLike) {
        createDiscussionLike()
      }
    }
  }

  const tooltip = useMemo(
    () =>
      discussion.user_liked
        ? discussion.likes > 1
          ? t("You and {{ count }} other liked", {
              defaultValue_plural: "You and {{ count }} others liked",
              count: discussion.user_liked
                ? discussion.likes - 1
                : discussion.likes
            })
          : t("You liked")
        : discussion.likes > 0
        ? t("{{ count }} like", {
            defaultValue_plural: "{{ count }} likes",
            count: discussion.likes
          })
        : t("Like"),
    [discussion.likes, discussion.user_liked]
  )

  return (
    <Tooltip title={tooltip}>
      <Button
        variant="ghost"
        onClick={(e) => {
          e.stopPropagation()
          handleToggleVote()
        }}
        colorPalette={discussion.user_liked ? "brand" : "blackAlpha"}
        disabled={!discussionPerms.canLike}
        borderRadius="full"
        padding={1}
        gap={0}
      >
        {discussion.user_liked ? (
          <Pathicon icon="heart-fill" />
        ) : (
          <Pathicon icon="heart" />
        )}
        {discussion.likes || null}
      </Button>
    </Tooltip>
  )
}

DiscussionVote.displayName = "DiscussionVote"

export default DiscussionVote
